import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NumeralPipe } from '@pipes/numeral.pipe';
import { BatteryIconComponent } from '@components/shared/battery-icon/battery-icon.component';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { ShipmentInfoPreparationViewModel } from '../shipment-info-preparation.model';

type FilterKey =
  | 'shipmentName'
  | 'departureName'
  | 'arrivalName'
  | 'deviceName'
  | 'battery';

@Component({
  selector: 'app-shipment-info-preparation-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    NumeralPipe,
    BatteryIconComponent,
    DirectivesModule,
  ],
  templateUrl: './shipment-info-preparation-table.component.html',
  styleUrl: './shipment-info-preparation-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShipmentInfoPreparationTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input({ required: true })
  public shipmentList: ShipmentInfoPreparationViewModel[] = [];

  @Input({ required: true })
  public tableIdPrefix = '';

  @Output() public opened =
    new EventEmitter<ShipmentInfoPreparationViewModel>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  public dataSource = new MatTableDataSource<ShipmentInfoPreparationViewModel>(
    [],
  );
  public readonly displayedColumns: string[] = [
    'shipmentName',
    'departureName',
    'arrivalName',
    'deviceName',
    'battery',
    'operation',
  ];

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'shipmentName',
    direction: 'asc',
  };

  public filterKeywords: Record<FilterKey, string> = {
    shipmentName: '',
    departureName: '',
    arrivalName: '',
    deviceName: '',
    battery: '',
  };

  public ngOnInit(): void {
    this.dataSource.data = this.shipmentList;
  }

  public ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // 初期表示で配送名昇順を適用する。
    if (!this.sort.active) {
      this.sort.active = this.initSortState.active;
    }
    if (!this.sort.direction) {
      this.sort.direction = this.initSortState.direction;
    }
    this.sort.sortChange.emit({
      active: this.sort.active,
      direction: this.sort.direction,
    });

    this.dataSource.sortingDataAccessor = (
      item: ShipmentInfoPreparationViewModel,
      property: string,
    ) => {
      if (property === 'battery') {
        return item.battery ?? -1;
      }

      const value = item[property as keyof ShipmentInfoPreparationViewModel];
      return value == null ? '' : `${value}`;
    };

    this.dataSource.filterPredicate = (
      element: ShipmentInfoPreparationViewModel,
    ) => {
      const shipmentName = this.matchKeyword(
        element.shipmentName,
        this.filterKeywords.shipmentName,
      );
      const departureName = this.matchKeyword(
        element.departureName,
        this.filterKeywords.departureName,
      );
      const arrivalName = this.matchKeyword(
        element.arrivalName,
        this.filterKeywords.arrivalName,
      );
      const deviceName = this.matchKeyword(
        element.deviceName,
        this.filterKeywords.deviceName,
      );
      const battery = this.matchKeyword(
        String(element.battery ?? ''),
        this.filterKeywords.battery,
      );

      return (
        shipmentName && departureName && arrivalName && deviceName && battery
      );
    };

    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['shipmentList'] !== undefined) {
      this.dataSource.data = changes['shipmentList'].currentValue;
    }
  }

  /** 設定済みのフィルター条件で絞り込みを実行する。 */
  public applyFilter(): void {
    this.dataSource.filter = 'dummy';
  }

  /** 指定したフィルター条件をクリアする。 */
  public clearFilter(key: FilterKey): void {
    this.filterKeywords[key] = '';
    this.applyFilter();
  }

  /** 値がキーワードと部分一致するか判定する。 */
  private matchKeyword(value: string, keyword: string): boolean {
    if (!keyword) {
      return true;
    }

    return value.trim().toLowerCase().includes(keyword.trim().toLowerCase());
  }
}
